import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, TextField, MenuItem, Snackbar, Alert } from '@mui/material';
import { useNav } from '../App';
import api from '../services/api';
import { formatAgeBand } from '../utils/formatAgeBand';
import { C, SP, SZ } from '../theme';
import { XPBar, RankUpToast, getRank } from '../components/ScoreAnimations';

const HAIR_OPTIONS = ['Short hair', 'Medium hair', 'Long hair', 'Curly hair', 'Wavy hair', 'Bald/shaved', 'Bald', 'Buzz cut', 'Other'];

const S = {
  root: {
    minHeight: '100vh', background: C.bgApp,
    padding: `16px ${SP.screenH}px`,
    display: 'flex', flexDirection: 'column', gap: `${SP.md}px`,
    paddingBottom: `${SZ.navHeight + SP.xl}px`,
  },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: `${SP.xs}px` },
  pageTitle: { fontSize: '22px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.3px' },
  editLink: { fontSize: '13px', fontWeight: 500, color: C.yellow, cursor: 'pointer', padding: '4px 0' },
  card: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '14px', padding: `${SP.cardPad}px` },
  cardHeader: { display: 'flex', alignItems: 'center', gap: `${SP.md}px`, marginBottom: `${SP.md}px` },
  avatar: {
    width: 48, height: 48, borderRadius: '50%',
    background: C.bgElevated, border: `1.5px solid ${C.teal}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', flexShrink: 0,
  },
  alias: { fontSize: '17px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.2px' },
  ageBand: { fontSize: '12px', color: C.textMuted, marginTop: '2px' },
  divider: { height: '0.5px', background: C.border, margin: `${SP.sm}px 0` },
  rowLabel: { fontSize: '11px', color: C.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '3px' },
  rowValue: { fontSize: '14px', color: C.textPrimary, fontWeight: 500 },
  chip: {
    display: 'inline-flex', alignItems: 'center',
    background: `${C.teal}18`, border: `0.5px solid ${C.teal}44`,
    borderRadius: '99px', padding: '3px 10px',
    fontSize: '11px', color: C.teal, fontWeight: 500,
  },
  checkinBar: {
    display: 'flex', alignItems: 'center', gap: `${SP.sm}px`,
    background: `${C.teal}12`, border: `0.5px solid ${C.teal}30`,
    borderRadius: '10px', padding: `${SP.sm}px ${SP.md}px`,
  },
  checkinDot: { width: 7, height: 7, borderRadius: '50%', background: C.teal, flexShrink: 0 },
  checkinText: { fontSize: '12px', color: C.teal, fontWeight: 500, flex: 1 },
  checkoutBtn: {
    fontSize: '11px', color: C.orange, border: `0.5px solid ${C.orange}55`,
    borderRadius: '8px', padding: '4px 10px', cursor: 'pointer',
    background: 'transparent', fontWeight: 500, fontFamily: 'inherit',
  },
  sectionTitle: {
    fontSize: '11px', fontWeight: 600, color: C.textMuted,
    letterSpacing: '0.8px', textTransform: 'uppercase',
    margin: `${SP.sm}px 0 ${SP.xs}px`,
  },
  cooldownNote: { fontSize: '11px', color: C.textDisabled, marginTop: '4px' },
  saveBtn: {
    width: '100%', height: '48px', background: C.yellow, color: C.yellowText,
    border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', marginTop: `${SP.sm}px`,
  },
  cancelBtn: {
    width: '100%', height: '40px', background: 'transparent', color: C.textMuted,
    border: `0.5px solid ${C.border}`, borderRadius: '12px', fontSize: '13px',
    cursor: 'pointer', fontFamily: 'inherit', marginTop: `${SP.xs}px`,
  },
  fieldError: { fontSize: '11px', color: C.orange, marginTop: '3px' },
  settingsRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer',
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: C.bgElevated, color: C.textPrimary, fontSize: '14px', borderRadius: '10px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.teal },
    '&.Mui-focused fieldset': { borderColor: C.teal },
  },
  '& .MuiInputLabel-root': { color: C.textMuted, fontSize: '13px' },
  '& .MuiInputLabel-root.Mui-focused': { color: C.teal },
  '& .MuiSelect-icon': { color: C.textMuted },
};

function hoursUntil(isoDate) {
  if (!isoDate) return 0;
  const h = Math.ceil((new Date(isoDate).getTime() + 86400000 - Date.now()) / 3600000);
  return h > 0 ? h : 0;
}

function Row({ label, value }) {
  return (
    <Box>
      <Typography sx={S.rowLabel}>{label}</Typography>
      <Typography sx={value ? S.rowValue : { fontSize: '14px', color: C.textDisabled, fontStyle: 'italic' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function Profile() {
  const navigate = useNav();
  const [mode, setMode] = useState('view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [form, setForm] = useState({ name: '', bio: '', alias: '', dob: '', hair_type: '', has_glasses: false, looking_for: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [rankUpShow,  setRankUpShow]  = useState(false);
  const [rankUpName,  setRankUpName]  = useState('');
  const prevScoreRef = React.useRef(null);

  // Detect rank-up when profile reloads after a game session
  React.useEffect(() => {
    if (!user) return;
    const score = user.game_score_total || 0;
    if (prevScoreRef.current !== null && score > prevScoreRef.current) {
      const before = getRank(prevScoreRef.current);
      const after  = getRank(score);
      if (after.name !== before.name) {
        setRankUpName(after.name);
        setRankUpShow(true);
        setTimeout(() => setRankUpShow(false), 2600);
      }
    }
    prevScoreRef.current = score;
  }, [user]);

  const loadProfile = useCallback(async () => {
    try {
      const r = await api.get('/auth/profile');
      setUser(r.data.user);
      setCheckin(r.data.checkin);
      const u = r.data.user;
      setForm({
        name: u.name || '',
        bio: u.current_description || '',
        alias: u.alias || '',
        dob: u.date_of_birth ? u.date_of_birth.split('T')[0] : '',
        hair_type: u.hair_type || '',
        has_glasses: !!u.has_glasses,
        looking_for: u.looking_for || '',
      });
    } catch {
      showToast('Could not load profile', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  function showToast(msg, severity = 'success') {
    setToast({ open: true, msg, severity });
  }

  async function handleCheckout() {
    try {
      await api.post('/checkout');
      setCheckin(null);
      showToast('Checked out');
    } catch {
      showToast('Checkout failed', 'error');
    }
  }

  async function handleSave() {
    setSaving(true);
    setFieldErrors({});
    try {
      const payload = {};
      if (form.name !== user.name) payload.name = form.name;
      if (form.bio !== (user.current_description || '')) payload.bio = form.bio;
      if (form.alias !== user.alias) payload.alias = form.alias;
      const dobFromDB = user.date_of_birth ? user.date_of_birth.split('T')[0] : '';
      if (form.dob !== dobFromDB) payload.dob = form.dob;
      if (form.hair_type !== (user.hair_type || '')) payload.hair_type = form.hair_type;
      if (form.has_glasses !== !!user.has_glasses) payload.has_glasses = form.has_glasses;
      if (form.looking_for !== user.looking_for) payload.looking_for = form.looking_for;

      if (Object.keys(payload).length === 0) { setMode('view'); return; }

      const r = await api.put('/auth/profile', payload);
      if (r.data.status === 'error') {
        setFieldErrors(r.data.errors || {});
        showToast('Some fields could not be saved', 'error');
      } else {
        showToast('Profile updated');
        setMode('view');
        loadProfile();
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) { setFieldErrors(errors); showToast('Some fields could not be saved', 'error'); }
      else showToast('Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  const Toast = (
    <Snackbar open={toast.open} autoHideDuration={2500}
      onClose={() => setToast(t => ({ ...t, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: `${SZ.navHeight + 12}px` }}>
      <Alert severity={toast.severity}
        sx={{ background: C.bgElevated, color: C.textPrimary, border: `0.5px solid ${C.border}` }}>
        {toast.msg}
      </Alert>
    </Snackbar>
  );

  if (loading) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: C.teal }} size={28} />
    </Box>
  );

  if (!user) return (
    <Box sx={S.root}>
      <Typography sx={{ color: C.textMuted, textAlign: 'center', marginTop: '40px' }}>
        Could not load profile.
      </Typography>
    </Box>
  );

  const aliasHoursLeft = hoursUntil(user.alias_last_updated_at);
  const appHoursLeft   = hoursUntil(user.appearance_last_updated_at);
  const prefHoursLeft  = hoursUntil(user.gender_pref_last_updated_at);
  const ageEditsLeft   = 2 - (user.age_edit_count || 0);

  // ── VIEW MODE ───────────────────────────────────────────────
  if (mode === 'view') return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Typography sx={S.pageTitle}>My Card</Typography>
        <Box sx={S.editLink} onClick={() => setMode('edit')}>Edit →</Box>
      </Box>

      {checkin && (
        <Box sx={S.checkinBar}>
          <Box sx={S.checkinDot} />
          <Typography sx={S.checkinText}>Checked in · {checkin.venue_name}</Typography>
          <button style={S.checkoutBtn} onClick={handleCheckout}>Check out</button>
        </Box>
      )}

      <Box sx={S.card}>
        <Box sx={S.cardHeader}>
          <Box sx={S.avatar}>
            <Typography sx={{ fontSize: '20px' }}>{(user.alias || '?').charAt(0).toUpperCase()}</Typography>
          </Box>
          <Box>
            <Typography sx={S.alias}>{user.alias}</Typography>
            <Typography sx={S.ageBand}>
              {formatAgeBand(user.date_of_birth)}
              {user.gender === 'male' ? ' · M' : user.gender === 'female' ? ' · F' : ''}
            </Typography>
            {(user.game_score_total > 0 || user.tournament_wins > 0) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '4px', flexWrap: 'wrap' }}>
                {user.game_score_total > 0 && (
                  <Typography sx={{ fontSize: '12px', color: C.yellow, fontWeight: 700 }}>
                    {user.game_score_total >= 1000000
                      ? (user.game_score_total/1000000).toFixed(1).replace(/\.0$/,'')+'M'
                      : user.game_score_total >= 1000
                        ? (user.game_score_total/1000).toFixed(1).replace(/\.0$/,'')+'K'
                        : user.game_score_total} pts
                  </Typography>
                )}
                {user.tournament_wins > 0 && (
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: 'rgba(254,114,16,0.10)',
                    border: '0.5px solid rgba(254,114,16,0.30)',
                    borderRadius: '99px', padding: '2px 8px',
                  }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="#FE7210" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4a2 2 0 0 1-2-2V5h4"/>
                      <path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/>
                      <path d="M6 9c0 4 2.5 7 6 8 3.5-1 6-4 6-8V3H6v6z"/>
                      <path d="M9 21h6"/><path d="M12 17v4"/>
                    </svg>
                    <Typography sx={{ fontSize: '11px', color: '#FE7210', fontWeight: 700 }}>
                      {user.tournament_wins}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {user.game_score_total > 0 && (
          <Box sx={{ mt: '10px', mb: '4px' }}>
            <XPBar score={user.game_score_total} animateOnMount />
          </Box>
        )}

        <RankUpToast show={rankUpShow} newRank={rankUpName} />

        <Box sx={S.divider} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SP.sm}px`, mt: `${SP.sm}px` }}>
          <Row label="Name" value={user.name} />
          {user.current_description && <Row label="Bio" value={user.current_description} />}
          <Row label="Looking for" value={
            user.looking_for === 'men' ? 'Men'
            : user.looking_for === 'women' ? 'Women'
            : user.looking_for === 'both' ? 'Men & Women' : null
          } />

          <Box sx={S.divider} />

          <Typography sx={S.rowLabel}>How to spot me</Typography>
          <Box sx={{ display: 'flex', gap: `${SP.xs}px`, flexWrap: 'wrap' }}>
            {user.hair_type && <Box sx={S.chip}>{user.hair_type}</Box>}
            {user.has_glasses ? <Box sx={S.chip}>Glasses</Box> : null}
            {!user.hair_type && !user.has_glasses && (
              <Typography sx={{ fontSize: '13px', color: C.textDisabled, fontStyle: 'italic' }}>
                Not set — tap Edit to add
              </Typography>
            )}
          </Box>
          {checkin?.location_area && <Row label="Spot tonight" value={checkin.location_area} />}
        </Box>
      </Box>

      {user.referral_code && (
        <Box sx={{ ...S.card, border: `0.5px solid ${C.teal}55`, borderRadius: '14px' }}>
          <Typography sx={S.sectionTitle} style={{ margin: 0, marginBottom: '10px' }}>Invite a friend</Typography>
          <Typography sx={S.rowLabel}>Your referral code</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '6px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: C.teal, letterSpacing: '3px', flex: 1 }}>
              {user.referral_code}
            </Typography>
            <button
              style={{ fontSize: '11px', color: C.teal, border: `0.5px solid ${C.teal}55`, borderRadius: '8px', padding: '4px 12px', cursor: 'pointer', background: 'transparent', fontWeight: 500, fontFamily: 'inherit' }}
              onClick={() => { if (navigator.clipboard) { navigator.clipboard.writeText(user.referral_code).then(() => showToast('Code copied!')); } else { showToast('Copy: ' + user.referral_code); } }}
            >Copy</button>
          </Box>
          <Typography sx={{ fontSize: '11px', color: C.textMuted, mt: '8px' }}>
            {user.invites_sent || 0} of 5 invites sent
          </Typography>
        </Box>
      )}

      <Box sx={{ ...S.card, ...S.settingsRow }} onClick={() => navigate('/settings')}>
        <Typography sx={{ fontSize: '14px', color: C.textPrimary }}>Settings</Typography>
        <Typography sx={{ color: C.textMuted, fontSize: '18px' }}>›</Typography>
      </Box>

      {Toast}
    </Box>
  );

  // ── EDIT MODE ───────────────────────────────────────────────
  return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Typography sx={S.pageTitle}>Edit Profile</Typography>
      </Box>

      <Typography sx={S.sectionTitle}>Always editable</Typography>
      <Box sx={S.card}>
        <TextField fullWidth label="Name" variant="outlined" size="small"
          value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          sx={{ ...inputSx, mb: 2 }} error={!!fieldErrors.name} helperText={fieldErrors.name} />
        <TextField fullWidth label="Bio (optional)" variant="outlined" size="small"
          multiline minRows={2} maxRows={4}
          value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
          inputProps={{ maxLength: 300 }} sx={inputSx} />
        <Typography sx={{ fontSize: '11px', color: C.textDisabled, textAlign: 'right', mt: '4px' }}>
          {form.bio.length}/300
        </Typography>
      </Box>

      <Typography sx={S.sectionTitle}>Alias</Typography>
      <Box sx={S.card}>
        <TextField fullWidth label="Alias" variant="outlined" size="small"
          value={form.alias} onChange={e => setForm(f => ({ ...f, alias: e.target.value }))}
          disabled={aliasHoursLeft > 0} sx={inputSx}
          error={!!fieldErrors.alias} helperText={fieldErrors.alias} />
        <Typography sx={S.cooldownNote}>
          {aliasHoursLeft > 0 ? `⏱ Locked for ${aliasHoursLeft}h more` : 'Can be changed once every 24h'}
        </Typography>
      </Box>

      <Typography sx={S.sectionTitle}>Appearance</Typography>
      <Box sx={S.card}>
        <TextField select fullWidth label="Hair" variant="outlined" size="small"
          value={form.hair_type} onChange={e => setForm(f => ({ ...f, hair_type: e.target.value }))}
          disabled={appHoursLeft > 0} sx={{ ...inputSx, mb: 2 }}
          error={!!fieldErrors.appearance} helperText={fieldErrors.appearance}>
          {HAIR_OPTIONS.map(h => (
            <MenuItem key={h} value={h} sx={{ fontSize: '14px', background: C.bgElevated, color: C.textPrimary }}>{h}</MenuItem>
          ))}
        </TextField>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.bgApp, borderRadius: '10px', padding: `${SP.sm}px ${SP.md}px`,
          border: `0.5px solid ${C.border}`, opacity: appHoursLeft > 0 ? 0.5 : 1,
          cursor: appHoursLeft === 0 ? 'pointer' : 'default',
        }} onClick={() => { if (appHoursLeft === 0) setForm(f => ({ ...f, has_glasses: !f.has_glasses })); }}>
          <Typography sx={{ fontSize: '14px', color: C.textPrimary }}>Usually wear glasses</Typography>
          <Box sx={{
            width: 40, height: 22, borderRadius: '11px',
            background: form.has_glasses ? C.yellow : C.bgElevated,
            border: `0.5px solid ${form.has_glasses ? C.yellow : C.border}`,
            position: 'relative', transition: 'background 0.2s',
          }}>
            <Box sx={{
              position: 'absolute', top: '3px', left: form.has_glasses ? '21px' : '3px',
              width: 16, height: 16, borderRadius: '50%',
              background: form.has_glasses ? C.yellowText : C.textDisabled,
              transition: 'left 0.2s',
            }} />
          </Box>
        </Box>
        <Typography sx={S.cooldownNote}>
          {appHoursLeft > 0 ? `⏱ Locked for ${appHoursLeft}h more` : 'Can be updated once every 24h'}
        </Typography>
      </Box>

      <Typography sx={S.sectionTitle}>Looking for</Typography>
      <Box sx={S.card}>
        <TextField select fullWidth label="Looking for" variant="outlined" size="small"
          value={form.looking_for} onChange={e => setForm(f => ({ ...f, looking_for: e.target.value }))}
          disabled={prefHoursLeft > 0} sx={inputSx}
          error={!!fieldErrors.looking_for} helperText={fieldErrors.looking_for}>
          {[{ value: 'women', label: 'Women' }, { value: 'men', label: 'Men' }, { value: 'both', label: 'Men & Women' }].map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '14px', background: C.bgElevated, color: C.textPrimary }}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Typography sx={S.cooldownNote}>
          {prefHoursLeft > 0 ? `⏱ Locked for ${prefHoursLeft}h more` : 'Can be changed once every 24h'}
        </Typography>
      </Box>

      <Typography sx={S.sectionTitle}>Date of birth</Typography>
      <Box sx={S.card}>
        <TextField fullWidth label="Date of birth" variant="outlined" size="small" type="date"
          value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
          disabled={ageEditsLeft <= 0} InputLabelProps={{ shrink: true }} sx={inputSx}
          error={!!fieldErrors.dob} helperText={fieldErrors.dob} />
        <Typography sx={S.cooldownNote}>
          {ageEditsLeft > 0 ? `${ageEditsLeft} lifetime edit${ageEditsLeft === 1 ? '' : 's'} remaining` : 'No edits remaining'}
        </Typography>
      </Box>

      <button style={{ ...S.saveBtn, ...(saving ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
        onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
      <button style={S.cancelBtn} onClick={() => { setMode('view'); setFieldErrors({}); }}>
        Cancel
      </button>

      {Toast}
    </Box>
  );
}
